/**
 * S-10 «Отчёт агента». Раздел 7.4. Тексты — как их проверил пост-валидатор; клиент их не переписывает.
 *
 * Лист доклада (01-direction): вывод → пункты с метками «Риск» / «Внимание» (иконка своей формы + слово
 * + цвет, вместо «❗») → «Что сделать» → «Что может пойти не так» → «Чего не хватает».
 */
import { useEffect, useState } from 'react'
import { fetchReport, type ReportFull } from '../api/client.js'
import { fetchAgentRun, startAgentRun, setReportVerdict, type AgentRun } from '../api/sections.js'
import { Icon, StatusTag } from '../components/icons/Icon.js'
import { hapticSuccess } from '../telegram/call.js'
import { Loading, Failed, Empty, useAnnounce, useScreenHead } from './common.js'
import { VerdictRow } from './ui.js'
import { agentTitle, dayMonth, insightTone } from './words.js'

/**
 * Пометка у действия «после вашего подтверждения». Потока подтверждений в приложении пока нет
 * (ни маршрута, ни кнопки), поэтому обещать кнопку нельзя — временный текст из 04-voice, 4.3.
 * Формулу «Штаб сделает после вашего подтверждения» включать вместе с потоком подтверждений.
 */
const NOTE_DRAFT = 'Черновик готов — скопируйте его и опубликуйте в кабинете WB.'
const NOTE_CONFIRM = 'Сделать это можно в кабинете WB: Штаб читает данные кабинета и сам ничего в нём не меняет.'

/** «Часть штрафов…» после «Что даст:» — со строчной; аббревиатуры («ДРР…») не трогаем. */
function lowerFirst(s: string): string {
  const a = s.charAt(0), b = s.charAt(1)
  return b !== '' && b === b.toLowerCase() && b !== b.toUpperCase() ? a.toLowerCase() + s.slice(1) : s
}

/** Копирование черновика: буфер обмена, а если WebView его не даёт — выделение и copy. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true }
  } catch { /* пробуем запасной путь */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0'
    document.body.appendChild(ta); ta.select()
    const ok = document.execCommand('copy')
    ta.remove()
    return ok
  } catch { return false }
}

function CopyButton({ text, testid }: { text: string; testid: string }): React.JSX.Element {
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle')
  const announce = useAnnounce()
  return (
    <button type="button" className="btn btn-soft btn-sm" data-testid={testid}
      onClick={() => {
        void copyText(text).then((ok) => {
          setState(ok ? 'ok' : 'fail'); setTimeout(() => setState('idle'), 2500)
          // Смена текста кнопки в фокусе диктор не объявляет — итог идёт в живую область оболочки.
          announce(ok ? { kind: 'ok', text: 'Черновик скопирован' } : { kind: 'fail', text: 'Не получилось скопировать — выделите текст черновика вручную.' })
        })
      }}>
      <Icon name={state === 'ok' ? 'action-confirm' : 'action-copy'} size={20} />
      {state === 'ok' ? 'Скопировано' : state === 'fail' ? 'Выделите текст вручную' : 'Скопировать'}
    </button>
  )
}

/**
 * «Обновить отчёт сейчас» — только владельцу. Отчёт считается ночью; если с тех пор многое изменилось,
 * пересчёт идёт через очередь (1–2 минуты). Готовый отчёт открывается на месте текущего.
 */
function Rerun({ agent, onFresh }: { agent: string; onFresh: (id: string) => void }): React.JSX.Element | null {
  const [run, setRun] = useState<AgentRun | null>(null)
  const [busy, setBusy] = useState(false)
  const [fresh, setFresh] = useState<string | null>(null)
  const announce = useAnnounce()
  const going = run?.last?.state === 'queued' || run?.last?.state === 'sent'
  useEffect(() => {
    let alive = true
    void fetchAgentRun(agent).then((r) => { if (alive && r.ok) setRun(r.data) })
    return () => { alive = false }
  }, [agent])
  useEffect(() => {
    if (!going) return
    const t = setInterval(() => {
      void fetchAgentRun(agent).then((r) => {
        if (!r.ok) return
        setRun(r.data)
        const l = r.data.last
        if (l?.state === 'done' && l.report_id !== null) { hapticSuccess(); setFresh(l.report_id); announce({ kind: 'ok', text: 'Отчёт пересчитан' }) }
        if (l?.state === 'failed') announce({ kind: 'fail', text: l.message ?? 'Пересчитать не получилось.' })
      })
    }, 5000)
    return () => clearInterval(t)
  }, [going, agent, announce])
  if (run === null) return null
  const start = async (): Promise<void> => {
    setBusy(true)
    const r = await startAgentRun(agent)
    setBusy(false)
    if (!r.ok) { announce({ kind: 'fail', text: r.error.message }); return }
    setFresh(null)
    setRun({ last: r.data.last, left: Math.max(0, run.left - 1) })
  }
  return (
    <section className="card card-pad gap-top" data-testid="rerun">
      {fresh !== null ? <>
        <p className="body-text" role="status">Отчёт пересчитан по свежим данным.</p>
        <button type="button" className="btn btn-block" data-testid="rerun-open" onClick={() => onFresh(fresh)}>Открыть новый отчёт</button>
      </> : going ? (
        <p className="body-text" role="status" data-testid="rerun-going">
          <Icon name="action-busy" size={20} className="ic-muted" /> Пересчитываю — обычно 1–2 минуты. Запрошено в {run.last!.at_display}.
        </p>
      ) : <>
        <p className="row-meta">Отчёт считается ночью. Если с тех пор многое изменилось, его можно пересчитать по свежим данным — это 1–2 минуты.</p>
        {run.last?.state === 'failed' && run.last.message !== null && <p className="row-meta" data-testid="rerun-failed">{run.last.message}</p>}
        <button type="button" className="btn btn-soft btn-block gap-top-s" data-testid="rerun-start" disabled={busy || run.left === 0}
          onClick={() => { void start() }}><Icon name="action-retry" size={20} />Обновить отчёт сейчас</button>
        <p className="row-meta gap-top-s">{run.left === 0 ? 'Пересчёты на сегодня закончились — плановый отчёт придёт утром.' : `Осталось на сегодня: ${run.left}`}</p>
      </>}
    </section>
  )
}

export function ScreenReport({ id, onAll, onFresh }: { id: string; onAll: () => void; onFresh: (id: string) => void }): React.JSX.Element {
  const [r, setR] = useState<ReportFull | null>(null)
  const [fail, setFail] = useState<{ status: number; message: string } | null>(null)
  const [n, setN] = useState(0)
  useEffect(() => {
    let alive = true
    setFail(null); setR(null)
    void fetchReport(id).then((x) => { if (alive) { if (x.ok) setR(x.data); else setFail({ status: x.status, message: x.error.message }) } })
    return () => { alive = false }
  }, [id, n])
  useScreenHead(r === null ? undefined : agentTitle(r.agent),
    r === null ? null : dayMonth(r.local_date) + (r.period_display ? ' · ' + r.period_display : '')
      + (r.manual_display ? ' · ' + r.manual_display : '') + (r.source === 'template' ? ' · по шаблону, без ИИ' : ''))

  const announce = useAnnounce()
  if (fail !== null && fail.status === 404) {
    return (
      <Empty icon="section-reports" title="Этого отчёта нет" text="Возможно, ссылка устарела.">
        <button type="button" className="btn btn-soft" data-testid="all-reports" onClick={onAll}>Все отчёты</button>
      </Empty>
    )
  }
  if (fail !== null) return <Failed message={fail.message} onRetry={() => setN((x) => x + 1)} />
  if (r === null) return <Loading label="Загрузка отчёта" rows={5} />
  const o = r.output ?? {}
  const missing = (o.missing_data ?? []).map((m) => (typeof m === 'string' ? m : m.why ? `${m.what} — ${m.why}` : m.what))
  const actions = o.actions ?? []
  const risks = o.risks ?? []
  return (
    <>
      <section className="card" data-testid="screen-body">
        {o.headline !== undefined && o.headline !== '' && <div className="report-lead"><h2>{o.headline}</h2></div>}
        {(o.insights ?? []).map((i, k) => {
          const tone = insightTone(i.severity)
          return (
            <div className="insight" key={k} data-testid={'insight-' + k}>
              {tone !== null && <StatusTag tone={tone} />}
              <p className="wb-body">{i.text}</p>
            </div>
          )
        })}
      </section>

      {actions.length > 0 && (
        <>
          <h2 className="sect wb-overline"><span>Что сделать</span></h2>
          <section className="card" data-testid="actions" role="list" aria-label="Что сделать">
            {actions.map((a, k) => {
              const draft = a.params?.text ?? null
              return (
                <div className="step" role="listitem" key={a.id ?? k}>
                  <span className="step-n" aria-hidden="true">{k + 1}</span>
                  <div className="step-main">
                    <p className="step-title"><span className="vh">{k + 1}. </span>{a.title}</p>
                    {a.expected_effect ? <p className="step-note"><b>Что даст:</b> {lowerFirst(a.expected_effect)}</p> : null}
                    {a.execution === 'needs_confirmation' && (
                      <p className="step-note" data-testid={'confirm-note-' + k}>{draft !== null ? NOTE_DRAFT : NOTE_CONFIRM}</p>
                    )}
                    {draft !== null && (
                      <div className="draft">
                        <span className="draft-label">Черновик ответа</span>
                        <span className="quote wb-prose" data-testid={'draft-' + k}>{draft}</span>
                        <div><CopyButton text={draft} testid={'copy-' + k} /></div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </section>
        </>
      )}

      {risks.length > 0 && (
        <>
          <h2 className="sect wb-overline"><span>Что может пойти не так</span></h2>
          <section className="card" data-testid="risks" role="list" aria-label="Что может пойти не так">
            {risks.map((t, k) => <div className="row" role="listitem" key={k}><span className="row-main"><span className="wb-body">{t}</span></span></div>)}
          </section>
        </>
      )}

      {missing.length > 0 && (
        <>
          <h2 className="sect wb-overline"><span>Чего не хватает</span></h2>
          <section className="card" data-testid="missing" role="list" aria-label="Чего не хватает">
            {missing.map((m, k) => (
              <div className="row has-ic" role="listitem" key={k}>
                {/* Нехватка данных — не риск: нейтральная иконка цвета «Пепел» (06-icons, 5). */}
                <Icon name="section-blind" className="ic-muted" />
                <span className="row-main"><span className="wb-body">{m}</span></span>
              </div>
            ))}
          </section>
        </>
      )}

      <section className="card card-pad gap-top">
        <VerdictRow key={r.id} value={r.my_verdict ?? null} testid="report-verdict" q="Отчёт был полезен?"
          onSet={async (v) => {
            const x = await setReportVerdict(r.id, v)
            if (!x.ok) announce({ kind: 'fail', text: x.error.message })
            return x.ok
          }} />
      </section>
      {r.can_rerun === true && <Rerun key={'rerun-' + r.id} agent={r.agent} onFresh={onFresh} />}
    </>
  )
}
